import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import { useRole } from "@/hooks/use-role";
import { useAuth } from "@/hooks/use-auth";
import { usePartnerStatus } from "@/hooks/use-partner-status";
import { painelPadrao, painelsDisponiveis, type PanelType } from "@/lib/painel";

// Reexportados para os arquivos que já importam daqui. O tipo e a regra moram
// em `@/lib/painel`, que não importa nada — ver o cabeçalho de lá.
export { painelPadrao, type PanelType };

interface ActivePanelState {
  activePanel: PanelType;
  setActivePanel: (panel: PanelType) => void;
  availablePanels: PanelType[];
  panelReady: boolean;
}

const ActivePanelContext = createContext<ActivePanelState>({
  activePanel: "user",
  setActivePanel: () => {},
  availablePanels: ["user"],
  panelReady: false,
});

const STORAGE_KEY = "gfi-active-panel";

export function ActivePanelProvider({ children }: { children: ReactNode }) {
  const { role, loading } = useRole();
  const { user } = useAuth();
  const { isPartner, imoveis, carregando: partnerCarregando } = usePartnerStatus();

  const availablePanels = useMemo<PanelType[]>(
    () => painelsDisponiveis(role, isPartner),
    [role, isPartner],
  );

  const [activePanel, setActivePanelState] = useState<PanelType>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as PanelType | null;
    return stored || "user";
  });

  // panelReady fica false até o role carregar e o painel ser validado/inicializado
  const [panelReady, setPanelReady] = useState(false);

  useEffect(() => {
    // Esperar TAMBÉM o cadastro de anunciante. Decidir com "ainda não sei"
    // abriria o Cliente e trocaria para o Anunciante na frente da pessoa — o
    // mesmo piscar que /planos teve ao ler bandeira pendente como desligada.
    if (loading || partnerCarregando) return;

    // Não gravar no localStorage enquanto não há usuário autenticado.
    // Caso contrário, o valor "user" seria persistido e bloquearia o redirect
    // para /superadmin ou /admin após o login, pois "user" é sempre válido em availablePanels.
    if (!user) {
      setPanelReady(true);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY) as PanelType | null;

    if (stored && availablePanels.includes(stored)) {
      // Escolha explícita do usuário — manter
      setActivePanelState(stored);
    } else {
      // Sem escolha armazenada, ou inválida para o estado atual da conta.
      const defaultPanel = painelPadrao(availablePanels, { temImoveis: imoveis > 0 });
      setActivePanelState(defaultPanel);
      localStorage.setItem(STORAGE_KEY, defaultPanel);
    }

    setPanelReady(true);
  }, [role, loading, availablePanels, user, imoveis, partnerCarregando]);

  const setActivePanel = (panel: PanelType) => {
    if (!availablePanels.includes(panel)) return;
    setActivePanelState(panel);
    localStorage.setItem(STORAGE_KEY, panel);
  };

  return (
    <ActivePanelContext.Provider value={{ activePanel, setActivePanel, availablePanels, panelReady }}>
      {children}
    </ActivePanelContext.Provider>
  );
}

export const useActivePanel = () => useContext(ActivePanelContext);
